import React, { useEffect, useState } from "react";
import { Loader2, Save, X } from "lucide-react";
import { http } from "@/hooks/useAdmin";

const DAYS = [[1, "Mon"], [2, "Tue"], [3, "Wed"], [4, "Thu"], [5, "Fri"], [6, "Sat"], [7, "Sun"]];
const FIELD = "rounded-xl border border-navy/10 bg-white px-3 py-2 text-sm text-navy outline-none focus:border-royal focus:ring-2 focus:ring-royal/20";
const LABEL = "block text-[11px] font-semibold uppercase tracking-wide text-ink/45";

export const SettingsView = ({ onUnauthorized }) => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [blockDate, setBlockDate] = useState("");

  useEffect(() => {
    let active = true;
    http.get("/admin/settings/availability")
      .then(({ data }) => { if (active) setS((prev) => prev || data); })
      .catch((e) => e.response?.status === 401 && onUnauthorized());
    return () => { active = false; };
  }, [onUnauthorized]);

  if (!s) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-navy/40" /></div>;

  const toggle = (key, v) => setS((x) => ({ ...x, [key]: x[key].includes(v) ? x[key].filter((d) => d !== v) : [...x[key], v] }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    try {
      const { all_modes, ...body } = s;
      const { data } = await http.put("/admin/settings/availability", body);
      setS({ ...data, all_modes });
      setMsg("Saved. Visitors now see the updated slots.");
    } catch (err) {
      setMsg(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-3xl space-y-6" data-testid="settings-form">
      <div className="rounded-2xl border border-navy/[0.08] bg-white p-6">
        <h3 className="font-display text-lg font-bold text-navy">Working days & hours</h3>
        <p className="mt-1 text-sm text-ink/55">Visitors can only book inside these windows (IST). These hours are also shown publicly on the Contact page. Clients automatically receive an email reminder ~24 hours before each scheduled appointment.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {DAYS.map(([d, l]) => (
            <button key={d} type="button" data-testid={`settings-day-${d}`} onClick={() => toggle("days", d)}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${s.days.includes(d) ? "bg-navy text-white" : "border border-navy/10 bg-white text-navy/60"}`}>{l}</button>
          ))}
        </div>
        <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div><label className={LABEL} htmlFor="st">Start</label><input id="st" type="time" data-testid="settings-start" value={s.start} onChange={(e) => setS({ ...s, start: e.target.value })} className={`${FIELD} mt-1 w-full`} /></div>
          <div><label className={LABEL} htmlFor="en">End</label><input id="en" type="time" data-testid="settings-end" value={s.end} onChange={(e) => setS({ ...s, end: e.target.value })} className={`${FIELD} mt-1 w-full`} /></div>
          <div><label className={LABEL} htmlFor="sl">Slot (min)</label>
            <select id="sl" data-testid="settings-slot" value={s.slot_minutes} onChange={(e) => setS({ ...s, slot_minutes: Number(e.target.value) })} className={`${FIELD} mt-1 w-full`}>
              {[15, 20, 30, 45, 60, 90].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div><label className={LABEL} htmlFor="no">Min notice (hrs)</label><input id="no" type="number" min={0} max={72} data-testid="settings-notice" value={s.min_notice_hours} onChange={(e) => setS({ ...s, min_notice_hours: Number(e.target.value) })} className={`${FIELD} mt-1 w-full`} /></div>
        </div>
        <div className="mt-4 max-w-[12rem]"><label className={LABEL} htmlFor="ah">Bookable days ahead</label><input id="ah" type="number" min={1} max={120} data-testid="settings-ahead" value={s.max_days_ahead} onChange={(e) => setS({ ...s, max_days_ahead: Number(e.target.value) })} className={`${FIELD} mt-1 w-full`} /></div>
      </div>

      <div className="rounded-2xl border border-navy/[0.08] bg-white p-6">
        <h3 className="font-display text-lg font-bold text-navy">Blocked dates</h3>
        <p className="mt-1 text-sm text-ink/55">Holidays or days off — no slots will be offered.</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {s.blocked_dates.map((d) => (
            <span key={d} className="inline-flex items-center gap-1 rounded-full bg-lightbg px-3 py-1 text-xs font-medium text-navy">{d}
              <button type="button" onClick={() => setS({ ...s, blocked_dates: s.blocked_dates.filter((x) => x !== d) })} aria-label={`Unblock ${d}`} className="text-ink/40 hover:text-red-500"><X className="h-3 w-3" /></button>
            </span>
          ))}
          <input type="date" data-testid="settings-block-date" value={blockDate} onChange={(e) => setBlockDate(e.target.value)} className={FIELD} />
          <button type="button" data-testid="settings-block-add" disabled={!blockDate} onClick={() => { if (!s.blocked_dates.includes(blockDate)) setS({ ...s, blocked_dates: [...s.blocked_dates, blockDate].sort() }); setBlockDate(""); }} className="rounded-xl border border-navy/15 px-3 py-2 text-sm font-semibold text-navy disabled:opacity-40">Block</button>
        </div>
      </div>

      <div className="rounded-2xl border border-navy/[0.08] bg-white p-6">
        <h3 className="font-display text-lg font-bold text-navy">Meeting modes & office</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {s.all_modes.map((m) => (
            <button key={m.key} type="button" data-testid={`settings-mode-${m.key}`} onClick={() => toggle("modes", m.key)}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${s.modes.includes(m.key) ? "bg-royal text-white" : "border border-navy/10 bg-white text-navy/60"}`}>{m.label}</button>
          ))}
        </div>
        <div className="mt-4"><label className={LABEL} htmlFor="addr">Office address (shown for in-person bookings)</label><input id="addr" data-testid="settings-address" value={s.office_address} onChange={(e) => setS({ ...s, office_address: e.target.value })} className={`${FIELD} mt-1 w-full`} /></div>
      </div>

      <div className="flex items-center gap-4">
        <button type="submit" data-testid="settings-save" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-navy shadow-gold disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save availability
        </button>
        {msg && <p data-testid="settings-msg" className="text-sm text-ink/60">{msg}</p>}
      </div>
    </form>
  );
};
